/**
 * Upload Questionnaire Modal
 * Allows users to upload a CSV with client name and custom fields
 */

import React, { useState, useRef } from 'react';
import { createQuestionnaire } from '../../services/questionnaireService';
import { CustomField } from '../../types';
import CustomFieldsEditor from '../CustomFieldsEditor';
import './UploadQuestionnaireModal.css';

interface UploadQuestionnaireModalProps {
  projectId: string;
  onClose: () => void;
  onUploadComplete: () => void;
  userId?: string;
  userName?: string;
}

const UploadQuestionnaireModal: React.FC<UploadQuestionnaireModalProps> = ({
  projectId,
  onClose,
  onUploadComplete,
  userId,
  userName,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [clientName, setClientName] = useState('');
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [showFieldsEditor, setShowFieldsEditor] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.csv')) {
      setFile(droppedFile);
      setError(null);
    } else {
      setError('Please drop a CSV file');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Please select a CSV file');
      }
    }
  };

  const handleUpload = async () => {
    if (!file || !clientName.trim()) {
      setError('Please provide a client name and select a CSV file');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const validFields = customFields.filter(f => f.name && f.description);
      await createQuestionnaire(
        file,
        projectId,
        clientName.trim(),
        validFields,
        userId,
        userName
      );
      onUploadComplete();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload questionnaire');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>✕</button>
        <h2>📤 Upload Questionnaire</h2>

        <div className="form-group">
          <label htmlFor="clientName">Client/Recipient Name *</label>
          <input
            id="clientName"
            type="text"
            value={clientName}
            onChange={e => setClientName(e.target.value)}
            placeholder="e.g., Acme Corp Security Review"
            disabled={isUploading}
          />
        </div>

        <div
          className={`file-drop-zone ${file ? 'has-file' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          {file ? (
            <div className="selected-file">
              <span>📄 {file.name}</span>
              <button onClick={e => { e.stopPropagation(); setFile(null); }}>✕</button>
            </div>
          ) : (
            <p>📁 Click or drag CSV file here</p>
          )}
        </div>

        <div className="csv-format-hint">
          <strong>CSV Format:</strong> Column header must be "Query" or "Question".
          Additional columns will be preserved.
        </div>

        <button
          className="fields-toggle"
          onClick={() => setShowFieldsEditor(!showFieldsEditor)}
        >
          📋 Custom Fields ({customFields.length})
          <span>{showFieldsEditor ? '▼' : '▶'}</span>
        </button>

        {showFieldsEditor && (
          <CustomFieldsEditor
            fields={customFields}
            onFieldsChange={setCustomFields}
            onClose={() => setShowFieldsEditor(false)}
          />
        )}

        {error && <div className="upload-error">{error}</div>}

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose} disabled={isUploading}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleUpload}
            disabled={!file || !clientName.trim() || isUploading}
          >
            {isUploading ? '⏳ Uploading...' : '🚀 Upload & Process'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadQuestionnaireModal;

